import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { StatusBanca } from '../../model/banca/Banca';
import useUserStore from '../../stores/userStore';
import { UsuarioUtils } from '../../model/Usuario/Usuario';
import { PermissaoEnum } from '../../model/Usuario/Permissao';
import { useBreadcrumbs } from '../../Routes/BreadcrumbContext';
import { createBreadcrumbs } from '../../utils/breadcrumbUtils';
import { ProcessoUsuarioProvider, useProcessoContext } from '../../Routes/context/ProcessoUsuarioContext';
import PermissaoWrapper from '../../components/PermissaoWrapper';
import { toast } from 'react-toastify';
import BackendErrorMessage from '../../components/BackendErrorMessage';
import CategoriaIcone from '../Processos/components/CategoriaIcone';

import { useBancasData } from './hooks/useBancasData';
import { useBancasFilter } from './hooks/useBancasFilter';
import { BancaViewModel } from '../../viewmodel/banca/BancaViewModel';
import TabelaBancas from './components/TabelaBancas';
import CriarBancaModal from './components/CriarBancaModal';
import StatusBancaModal from './components/StatusBancaModal';
import ComposicaoBancaModal from './components/ComposicaoBancaModal';
import DefinirDataModal from './components/DefinirDataModal';
import ConvocarBancaModal from './components/ConvocarBancaModal';
import DefinirSalaModal from './components/DefinirSalaModal';
import EmergenciaModal from './components/EmergenciaModal';
import CancelarBancaModal from './components/CancelarBancaModal';
import FecharBancaModal from './components/FecharBancaModal';
import AbrirIntegracaoModal from './components/AbrirIntegracaoModal';
import ConfirmarIntegracaoModal from './components/ConfirmarIntegracaoModal';
import ConfirmarEnvioTermoBancaModal from './components/ConfirmarEnvioTermoBancaModal';
import AlterarStatusBancaModal from './components/AlterarStatusBancaModal';
import { EnviarEmailHeteroidentificacao } from '../../viewmodel/ComunicacaoCandidato/EmailHeteroidentificacao';
import api from '../../api';
import HelpDrawer from './components/HelpDrawer';
import { BsQuestionCircle } from 'react-icons/bs';
import './index.css';

const BancasNova: React.FC = () => {
  const { processoId } = useParams<{ processoId: string }>();

  if (!processoId) {
    return <div className="bancas-container">
      <div className="alert alert-danger">Erro: Processo ID não fornecido</div>
    </div>;
  }

  return (
    <ProcessoUsuarioProvider processoId={processoId}>
      <BancasNovaContent />
    </ProcessoUsuarioProvider>
  );
};

const BancasNovaContent: React.FC = () => {
  const { setBreadcrumbs } = useBreadcrumbs();
  const { processoId } = useParams<{ processoId: string }>();
  const { processo } = useProcessoContext();
  const usuario = useUserStore((state: any) => state.usuario);
  const [showCriarModal, setShowCriarModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<StatusBanca | null>(null);
  const [showComposicaoModal, setShowComposicaoModal] = useState(false);
  const [selectedBanca, setSelectedBanca] = useState<any>(null);
  const [showDataModal, setShowDataModal] = useState(false);
  const [showConvocarModal, setShowConvocarModal] = useState(false);
  const [showSalaModal, setShowSalaModal] = useState(false);
  const [showEmergenciaModal, setShowEmergenciaModal] = useState(false);
  const [showCancelarModal, setShowCancelarModal] = useState(false);
  const [showFecharModal, setShowFecharModal] = useState(false);
  const [showAbrirIntegracaoModal, setShowAbrirIntegracaoModal] = useState(false);
  const [showFinalizarIntegracaoModal, setShowFinalizarIntegracaoModal] = useState(false);
  const [showEnviarTermosModal, setShowEnviarTermosModal] = useState(false);
  const [showAlterarStatusModal, setShowAlterarStatusModal] = useState(false);
  const [showHelpDrawer, setShowHelpDrawer] = useState(false);
  const [isLoadingAta, setIsLoadingAta] = useState(false);
  const [isLoadingAtaValidacaoAnterior, setIsLoadingAtaValidacaoAnterior] = useState(false);
  const [ataValidacaoAnteriorResumo, setAtaValidacaoAnteriorResumo] = useState<{
    totalCandidatosValidacaoAnterior: number;
    todasBancasEncerradas: boolean;
    podeGerarAta: boolean;
  } | null>(null);

  const temPermissao = usuario && UsuarioUtils.verificarTodasPermissoes(usuario, [PermissaoEnum.CONSULTAR_BANCA]);
  
  const { bancas, tiposBanca, loading, currentPage, totalPages, totalElements, pageSize, refetchBancas, goToPage } = useBancasData(processo?.id, temPermissao);
  const { 
    bancasProcessadas, 
    ocultarFinalizadas, 
    setOcultarFinalizadas, 
    sortedColumn, 
    sortDirection, 
    handleSort 
  } = useBancasFilter(bancas);

  const handleStatusClick = (status: StatusBanca) => {
    setSelectedStatus(status);
    setShowStatusModal(true);
  };

  const carregarResumoAtaValidacaoAnterior = async () => {
    if (!processo?.id) return;

    try {
      const resumo = await BancaViewModel.getAtaValidacaoAnteriorResumo(processo.id);
      setAtaValidacaoAnteriorResumo(resumo);
    } catch (error) {
      setAtaValidacaoAnteriorResumo(null);
    }
  };

  const atualizarTela = async () => {
    await Promise.resolve(refetchBancas());
    await carregarResumoAtaValidacaoAnterior();
  };

  const handleComposicaoClick = (banca: any) => {
    setSelectedBanca(banca);
    setShowComposicaoModal(true);
  };

  const handleDataClick = (banca: any) => {
    setSelectedBanca(banca);
    setShowDataModal(true);
  };

  const handleConvocarClick = (banca: any) => {
    setSelectedBanca(banca);
    setShowConvocarModal(true);
  };

  const handleConvocacaoSucesso = (bancaAtualizada: any) => {
    atualizarTela();
  };

  const handleSalaClick = (banca: any) => {
    setSelectedBanca(banca);
    setShowSalaModal(true);
  };

  const handleEmergenciaClick = (banca: any) => {
    setSelectedBanca(banca);
    setShowEmergenciaModal(true);
  };

  const handleCancelarClick = (banca: any) => {
    setSelectedBanca(banca);
    setShowCancelarModal(true);
  };

  const handleFecharClick = (banca: any) => {
    setSelectedBanca(banca);
    setShowFecharModal(true);
  };

  const handleAbrirIntegracaoClick = (banca: any) => {
    setSelectedBanca(banca);
    setShowAbrirIntegracaoModal(true);
  };

  const handleFinalizarIntegracaoClick = (banca: any) => {
    setSelectedBanca(banca);
    setShowFinalizarIntegracaoModal(true);
  };

  const handleEnviarTermosClick = async (banca: any) => {
    try {
      const resultado = await EnviarEmailHeteroidentificacao.enviarEmailResultadoCandidato({ 
        bancaId: banca.id, 
        apenasContagem: true 
      });
      
      if (resultado.totalElegivel === 0) {
        toast.warning('Nenhum candidato elegível para receber o termo');
        return;
      }

      setSelectedBanca({ ...banca, candidatosComEmail: resultado.totalElegivel });
      setShowEnviarTermosModal(true);
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Erro ao verificar candidatos';
      toast.error(errorMsg);
    }
  };

  const handleAlterarStatusClick = (banca: any) => {
    setSelectedBanca(banca);
    setShowAlterarStatusModal(true);
  };

  const handleAlterarStatusConfirm = async (novoStatus: string) => {
    if (!selectedBanca) return;
    setShowAlterarStatusModal(false);
    
    try {
      await api.patch(`/bancas/${selectedBanca.id}/status`, { status: novoStatus });
      toast.success('Status da banca atualizado com sucesso!');
      atualizarTela();
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Erro ao atualizar status';
      toast.error(errorMsg);
    }
  };

  const handleAtaClick = async (banca: any) => {
    setIsLoadingAta(true);
    try {
      await BancaViewModel.getAtaBanca(processo?.id!, banca.id, processo?.nome!, banca.tipoBanca.nome!);
      toast.success('Iniciando download...');
    } catch (error) {
      console.error('Erro ao gerar Ata:', error);
      toast.error(BackendErrorMessage(error));
    } finally {
      setIsLoadingAta(false);
    }
  };

  const handleGerarAtaValidacaoAnterior = async () => {
    if (!processo?.id || !processo?.nome) return;

    setIsLoadingAtaValidacaoAnterior(true);
    try {
      await BancaViewModel.getAtaValidacaoAnteriorPdf(processo.id, processo.nome);
      toast.success('Iniciando download da ATA de validações anteriores...');
      await carregarResumoAtaValidacaoAnterior();
    } catch (error) {
      toast.error(BackendErrorMessage(error));
    } finally {
      setIsLoadingAtaValidacaoAnterior(false);
    }
  };

  const handleCancelarConfirm = async () => {
    if (!selectedBanca) return;
    
    try {
      await BancaViewModel.cancelarBanca(selectedBanca.id);
      atualizarTela();
    } catch (error) {
      throw error; // O modal vai capturar e mostrar o erro
    }
  };

  const handleFecharConfirm = async () => {
    if (!selectedBanca) return;
    
    try {
      await BancaViewModel.fecharBanca(selectedBanca.id);
      atualizarTela();
      setShowFecharModal(false);
    } catch (error) {
      throw error; // O modal vai capturar e mostrar o erro
    }
  };

  const handleAbrirIntegracaoConfirm = async () => {
    if (!selectedBanca) return;
    
    try {
      await BancaViewModel.abrirIntegracao(selectedBanca.id);
      atualizarTela();
      setShowAbrirIntegracaoModal(false);
      toast.success('Banca aberta para integração com sucesso!');
    } catch (error) {
      throw error; // O modal vai capturar e mostrar o erro
    }
  };

  const handleFinalizarIntegracaoConfirm = async () => {
    if (!selectedBanca) return;
    
    try {
      await BancaViewModel.finalizarIntegracao(selectedBanca.id);
      atualizarTela();
      setShowFinalizarIntegracaoModal(false);
      toast.success('Integração finalizada com sucesso!');
    } catch (error) {
      throw error; // O modal vai capturar e mostrar o erro
    }
  };

  const handleEnviarTermosConfirm = async () => {
    if (!selectedBanca) return;
    setShowEnviarTermosModal(false);
    
    try {
      const resultado = await EnviarEmailHeteroidentificacao.enviarEmailResultadoCandidato({ bancaId: selectedBanca.id });
      const totalEnviados = resultado.emailsEnviadosValidado + resultado.emailsEnviadosNaoValidado + resultado.emailsEnviadosInconclusivo;
      
      if (totalEnviados > 0) {
        toast.success(
          `Termos enviados com sucesso! ` +
          `Validados: ${resultado.emailsEnviadosValidado} | ` +
          `Não Validados: ${resultado.emailsEnviadosNaoValidado} | ` +
          `Inconclusivos: ${resultado.emailsEnviadosInconclusivo}`,
          { autoClose: 5000 }
        );
      } else {
        toast.info('Nenhum termo foi enviado. Verifique se há candidatos com resultado e e-mail cadastrado.');
      }
      
      atualizarTela();
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Erro ao enviar termos';
      toast.error(errorMsg);
    }
  };

  useEffect(() => {
    setBreadcrumbs(createBreadcrumbs.fromProcessos([
      { path: `/processos/${processoId}/bancas`, breadcrumb: 'Bancas' },
    ]));
  }, [setBreadcrumbs, processoId]);

  useEffect(() => {
    if (temPermissao && processo?.id) {
      carregarResumoAtaValidacaoAnterior();
    }
  }, [temPermissao, processo?.id, bancas.length]);



  if (!temPermissao) {
    return (
      <div className="bancas-container">
        <div className="alert alert-warning">Você não tem permissão para consultar bancas.</div>
      </div>
    );
  }

  return (
    <HelpDrawer isOpen={showHelpDrawer} onClose={() => setShowHelpDrawer(false)}>
    <div className="bancas-container">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h2>
          {processo?.editais && processo.editais.length > 0 && processo.editais[0].categoria && (
            <CategoriaIcone
              categoria={processo.editais[0].categoria}
              size={24}
              showTooltip={true}
            />
          )}
          {` `}Bancas de {processo?.nome}
        </h2>
        <button
          className="btn-pagination"
          onClick={() => setShowHelpDrawer(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '13px',
          }}
          title="Abrir painel de ajuda"
        >
          <BsQuestionCircle /> Ajuda
        </button>
      </div>
      
      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
          <input 
            type="checkbox" 
            checked={ocultarFinalizadas}
            onChange={(e) => setOcultarFinalizadas(e.target.checked)}
          />
          Ocultar bancas finalizadas
        </label>
      </div>

      {ataValidacaoAnteriorResumo && ataValidacaoAnteriorResumo.totalCandidatosValidacaoAnterior > 0 && (
        <div className="ata-validacao-anterior-alert">
          <div>
            <div className="ata-validacao-anterior-alert-title">
              Existem {ataValidacaoAnteriorResumo.totalCandidatosValidacaoAnterior} candidato(s) com validação anterior neste processo
            </div>
            <div className="ata-validacao-anterior-alert-subtitle">
              Gere a ATA consolidada específica para registrar esses candidatos no processo atual.
            </div>
            {!ataValidacaoAnteriorResumo.todasBancasEncerradas && (
              <div className="ata-validacao-anterior-alert-warning">
                A geração será habilitada apenas quando todas as bancas estiverem FECHADA, FINALIZADA ou CANCELADA.
              </div>
            )}
          </div>
          <button
            className="ata-validacao-anterior-button"
            onClick={handleGerarAtaValidacaoAnterior}
            disabled={!ataValidacaoAnteriorResumo.podeGerarAta || isLoadingAtaValidacaoAnterior}
          >
            {isLoadingAtaValidacaoAnterior ? 'Gerando ATA...' : 'Gerar ATA de validados anteriores'}
          </button>
        </div>
      )}

      <TabelaBancas 
        bancas={bancasProcessadas}
        loading={loading}
        onBancaUpdated={atualizarTela}
        sortedColumn={sortedColumn}
        sortDirection={sortDirection}
        onSort={handleSort}
        onStatusClick={handleStatusClick}
        onComposicaoClick={handleComposicaoClick}
        onDataClick={handleDataClick}
        onConvocarClick={handleConvocarClick}
        onSalaClick={handleSalaClick}
        onEmergenciaClick={handleEmergenciaClick}
        onAtaClick={handleAtaClick}
        onCancelarClick={handleCancelarClick}
        onFecharClick={handleFecharClick}
        onAbrirIntegracaoClick={handleAbrirIntegracaoClick}
        onFinalizarIntegracaoClick={handleFinalizarIntegracaoClick}
        onEnviarTermosClick={handleEnviarTermosClick}
        onAlterarStatusClick={handleAlterarStatusClick}
        processoId={processoId}
      />
      
      <div className="pagination-container" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.CADASTRAR_BANCA]}>
          <button 
            className="btn-pagination" 
            style={{ 
              backgroundColor: '#28a745', 
              color: 'white', 
              border: '1px solid #28a745',
              fontWeight: '500'
            }}
            onClick={() => setShowCriarModal(true)}
          >
            + Nova Banca
          </button>
        </PermissaoWrapper>

        {totalPages > 1 && (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button 
              className="btn-pagination"
              onClick={() => goToPage(0)}
              disabled={currentPage === 0}
            >
              Primeira
            </button>
            <button 
              className="btn-pagination"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 0}
            >
              Anterior
            </button>
            <span style={{ fontSize: '14px' }}>
              Página {currentPage + 1} de {totalPages} ({totalElements} bancas)
            </span>
            <button 
              className="btn-pagination"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage >= totalPages - 1}
            >
              Próxima
            </button>
            <button 
              className="btn-pagination"
              onClick={() => goToPage(totalPages - 1)}
              disabled={currentPage >= totalPages - 1}
            >
              Última
            </button>
          </div>
        )}
      </div>

      {showCriarModal && (
        <CriarBancaModal
          tiposBanca={tiposBanca}
          onClose={() => setShowCriarModal(false)}
          onBancaCriada={atualizarTela}
        />
      )}

      {showStatusModal && selectedStatus && (
        <StatusBancaModal
          status={selectedStatus}
          onClose={() => setShowStatusModal(false)}
        />
      )}

      {showComposicaoModal && selectedBanca && (
        <ComposicaoBancaModal
          banca={selectedBanca}
          onClose={() => setShowComposicaoModal(false)}
          isConsultarDisponibilidadeDisabled={selectedBanca.status !== StatusBanca.INDEFINIDA}
          isDefinicaoMembrosDisabled={selectedBanca.status === StatusBanca.INDEFINIDA}
          hasMembrosConfirmados={false}
          hasTodosMembrosConsultados={false}
          membros={[]}
        />
      )}

      {showDataModal && selectedBanca && (
        <DefinirDataModal
          banca={selectedBanca}
          onClose={() => setShowDataModal(false)}
          onSaveSuccess={atualizarTela}
        />
      )}

      {showConvocarModal && selectedBanca && (
        <ConvocarBancaModal
          banca={selectedBanca}
          onClose={() => setShowConvocarModal(false)}
          onConvocacaoSucesso={handleConvocacaoSucesso}
        />
      )}

      {showSalaModal && selectedBanca && (
        <DefinirSalaModal
          banca={selectedBanca}
          processoId={processoId!}
          onClose={() => setShowSalaModal(false)}
          onSaveSuccess={atualizarTela}
        />
      )}

      {showEmergenciaModal && selectedBanca && (
        <EmergenciaModal
          banca={selectedBanca}
          onClose={() => setShowEmergenciaModal(false)}
          onSaveSuccess={atualizarTela}
        />
      )}

      {showCancelarModal && selectedBanca && (
        <CancelarBancaModal
          banca={selectedBanca}
          onClose={() => setShowCancelarModal(false)}
          onConfirm={handleCancelarConfirm}
        />
      )}

      {showFecharModal && selectedBanca && (
        <FecharBancaModal
          banca={selectedBanca}
          onClose={() => setShowFecharModal(false)}
          onConfirm={handleFecharConfirm}
        />
      )}

      {showAbrirIntegracaoModal && selectedBanca && (
        <AbrirIntegracaoModal
          banca={selectedBanca}
          onClose={() => setShowAbrirIntegracaoModal(false)}
          onConfirm={handleAbrirIntegracaoConfirm}
        />
      )}

      {showFinalizarIntegracaoModal && selectedBanca && (
        <ConfirmarIntegracaoModal
          banca={selectedBanca}
          processo={processo}
          onClose={() => setShowFinalizarIntegracaoModal(false)}
          onConfirm={handleFinalizarIntegracaoConfirm}
        />
      )}

      {showEnviarTermosModal && selectedBanca && (
        <ConfirmarEnvioTermoBancaModal
          show={showEnviarTermosModal}
          handleClose={() => setShowEnviarTermosModal(false)}
          handleConfirm={handleEnviarTermosConfirm}
          quantidade={selectedBanca.candidatosComEmail || 0}
          processoId={processoId!}
          bancaId={selectedBanca.id}
        />
      )}

      {showAlterarStatusModal && selectedBanca && (
        <AlterarStatusBancaModal
          show={showAlterarStatusModal}
          handleClose={() => setShowAlterarStatusModal(false)}
          handleConfirm={handleAlterarStatusConfirm}
          statusAtual={selectedBanca.status}
        />
      )}
    </div>
    </HelpDrawer>
  );
};

export default BancasNova;
